/**
 * File: journalGroupsUtils.ts
 * Path: apps/admin/src/shared/utils/journalGroupsUtils.ts
 * Description: How All entries is sorted out so it can be read: each journal
 * gets a kind (sale, payment in, expense or other), the list can be narrowed
 * by kind, year and search words, and what is left is grouped by month with
 * a total for each month. Pure functions, no screen code.
 */

import type { Journal, LedgerAccount } from "@idk/config";

export type EntryKind = "sales" | "payments" | "expenses" | "other";

export const ENTRY_KINDS: { kind: EntryKind; label: string }[] = [
  { kind: "sales", label: "Sales" },
  { kind: "payments", label: "Payments in" },
  { kind: "expenses", label: "Expenses" },
  { kind: "other", label: "Other" },
];

/** Debtors: what customers owe. A journal that credits it is a customer paying. */
const DEBTORS_CODE = "1100";

/** Opening balances and reversals are always "Other"; the rest is read from the accounts they touch. */
export const entryKind = (journal: Journal, accounts: Map<string, LedgerAccount>): EntryKind => {
  if (journal.source === "opening" || journal.source === "reversal") return "other";
  const touched = journal.lines.map((line) => ({ line, account: accounts.get(line.accountId) }));
  if (touched.some(({ line, account }) => account?.type === "income" && line.creditCents > 0)) return "sales";
  if (touched.some(({ line, account }) => account?.code === DEBTORS_CODE && line.creditCents > 0)) return "payments";
  if (touched.some(({ line, account }) => account?.type === "expense" && line.debitCents > 0)) return "expenses";
  return "other";
};

export interface MonthGroup {
  /** "2026-03" */
  month: string;
  journals: Journal[];
  totalCents: number;
}

export interface EntryFilter {
  kind: EntryKind | "all";
  /** "2026", or "all". Calendar year of the journal date. */
  year: string;
  search: string;
}

const matchesSearch = (journal: Journal, words: string[]): boolean => {
  if (words.length === 0) return true;
  const haystack = `${journal.number} ${journal.narration} ${journal.party ?? ""} ${journal.project ?? ""} ${journal.date}`.toLowerCase();
  return words.every((word) => haystack.includes(word));
};

export const filterEntries = (
  journals: Journal[],
  accounts: Map<string, LedgerAccount>,
  filter: EntryFilter,
): Journal[] => {
  const words = filter.search.toLowerCase().split(/\s+/).filter(Boolean);
  return journals.filter(
    (j) =>
      (filter.kind === "all" || entryKind(j, accounts) === filter.kind) &&
      (filter.year === "all" || j.date.startsWith(filter.year)) &&
      matchesSearch(j, words),
  );
};

/** How many of each kind there are in the given years, for the tab counts. */
export const countByKind = (
  journals: Journal[],
  accounts: Map<string, LedgerAccount>,
): Record<EntryKind | "all", number> => {
  const counts: Record<EntryKind | "all", number> = { all: journals.length, sales: 0, payments: 0, expenses: 0, other: 0 };
  for (const j of journals) counts[entryKind(j, accounts)] += 1;
  return counts;
};

/** The calendar years that have entries, newest first. */
export const entryYears = (journals: Journal[]): string[] =>
  [...new Set(journals.map((j) => j.date.slice(0, 4)))].sort().reverse();

/** Newest month first; inside a month, newest entry first. */
export const groupByMonth = (journals: Journal[]): MonthGroup[] => {
  const groups = new Map<string, MonthGroup>();
  for (const j of journals) {
    const month = j.date.slice(0, 7);
    const group = groups.get(month) ?? { month, journals: [], totalCents: 0 };
    group.journals.push(j);
    group.totalCents += j.totalCents;
    groups.set(month, group);
  }
  return [...groups.values()]
    .sort((a, b) => b.month.localeCompare(a.month))
    .map((g) => ({
      ...g,
      journals: [...g.journals].sort((a, b) => b.date.localeCompare(a.date) || b.number - a.number),
    }));
};
